import { useEffect, useState } from "react";
import { X, Loader2, Trophy, Clapperboard } from "lucide-react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";

// Shown when a creator taps their OWN avatar but hasn't unlocked their channel page yet.
// Displays a live "X of N videos" progress counter toward the unlock.
export const CreatorUnlockModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open) return;
    setData(null);
    api.get("/me/creator-page").then(({ data }) => setData(data)).catch(() => setData({ error: true }));
  }, [open]);

  if (!open) return null;

  const count = data?.published_count ?? 0;
  const min = data?.min_required ?? 10;
  const pct = Math.min(100, Math.round((count / min) * 100));
  const eligible = data?.eligible;
  const remaining = Math.max(0, min - count);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" onClick={onClose} data-testid="creator-unlock-modal">
      <div className="bg-white rounded-3xl w-full max-w-sm shadow-2xl overflow-hidden border border-[#dfe7d8] my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="relative px-6 py-5 text-white" style={{ background: "linear-gradient(135deg,#5d8a52,#3f6238)" }}>
          <button data-testid="creator-unlock-close" onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/15 transition-colors"><X size={20} /></button>
          <div className="flex items-center gap-2.5">
            <span className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0"><Trophy size={22} /></span>
            <div>
              <h3 className="text-lg font-extrabold leading-tight">Unlock your channel</h3>
              <p className="text-[13px] font-semibold text-white/85 leading-tight">Your own page, curating all your videos.</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-6">
          {!data ? (
            <div className="py-8 flex justify-center"><Loader2 size={26} className="spin text-[#5d8a52]" /></div>
          ) : (
            <>
              <p className="text-sm text-[#4b4860] text-center">
                Publish <span className="font-extrabold text-[#3f6238]">{min} videos</span> to the Community and you'll unlock your own creator channel — a curated page with your banner, bio and every video you've made.
              </p>

              <div className="mt-5 text-center">
                <div className="text-4xl font-extrabold text-[#171426] tabular-nums leading-none" data-testid="creator-unlock-count">
                  {count}<span className="text-[#9a96ac] text-2xl"> / {min}</span>
                </div>
                <div className="text-xs font-bold text-[#5d8a52] mt-1">videos published</div>
              </div>

              <div className="mt-4 h-3 rounded-full bg-[#eef3ea] overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#5d8a52,#3f6238)" }} data-testid="creator-unlock-bar" />
              </div>

              {eligible ? (
                <button data-testid="creator-unlock-view" onClick={() => { onClose(); navigate(`/creator/${data.username}`); }}
                  className="w-full mt-6 inline-flex items-center justify-center gap-2 font-extrabold text-white py-3 rounded-2xl"
                  style={{ background: "linear-gradient(135deg,#5d8a52,#3f6238)" }}>
                  <Clapperboard size={18} /> View my channel
                </button>
              ) : (
                <p className="text-center text-sm font-bold text-[#3f6238] mt-5" data-testid="creator-unlock-remaining">
                  {remaining} more to go — keep creating! 🎬
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreatorUnlockModal;
